// barba/barba-prevent-handler.js

/**
 * Determines if Barba.js should prevent handling a link click.
 * @param {object} eventData - The event data object from Barba.
 * @param {HTMLAnchorElement} eventData.el - The clicked anchor element.
 * @returns {boolean} True if Barba should be prevented, false otherwise.
 */
export function barbaPreventHandler({ el }) {
  // If the clicked element or its href is missing, or href is empty, Barba should not handle it.
  if (!el || typeof el.href === 'undefined' || el.href === null || el.href === '') {
    return true; // Prevent Barba from handling
  }

  // Prevent Barba from handling clicks on top-level menu items that have children.
  // This allows them to act as dropdown toggles without navigating.
  const menuItem = el.closest('li.menu-item-has-children');
  // This condition is now more specific: it only prevents Barba if the clicked link (`el`)
  // is a *direct child* of the `li.menu-item-has-children`. This correctly targets
  // the parent toggle link, while allowing links inside the sub-menu to be handled by Barba.
  if (menuItem && el.parentElement === menuItem) {
    return true; // Prevent Barba for the parent toggle link.
  }

  if (el.closest && el.closest('#wpadminbar')) return true; // WordPress Admin Bar
  if (el.target === '_blank') return true; // Links opening in a new tab
  if (/\.(pdf|zip|jpg|jpeg|png|gif|xml|svg|docx|xlsx|pptx|mp4|webm|mp3|ogg)$/i.test(el.href)) return true; // File downloads
  if (el.hasAttribute && el.hasAttribute('data-barba-prevent')) return true; // Explicitly prevented links
  if (el.href.includes('wp-login.php') || el.href.includes('logout')) return true; // WP login/logout
  // If Lenis handles all same-page anchors, you might not need to prevent them for Barba.
  // if (el.pathname === window.location.pathname && el.hash && el.hash !== '#') return true;

  return false; // If none of the above, Barba handles it.
}


/**
 * Manually attempts to synchronize the document.body.className with the
 * class attribute found on the <body> tag of the fetched HTML content.
 * This is typically a workaround if Barba.js's `sync: true` option
 * isn't behaving as expected for body classes.
 * @param {string} nextHtml - The full HTML string of the next page.
 * @param {boolean} debug - Whether to log debugging information.
 */
export function syncManualBodyClasses(nextHtml, debug = false) {
  if (debug) {
    console.log('[Barba Debug] syncManualBodyClasses: Attempting manual body class sync.');
  }

  const nextHtmlBodyMatch = nextHtml.match(/<body([^>]*)>/i);
  if (nextHtmlBodyMatch && nextHtmlBodyMatch[1]) {
    const nextHtmlBodyClassMatch = nextHtmlBodyMatch[1].match(/class="([^"]*)"/i);
    if (nextHtmlBodyClassMatch && nextHtmlBodyClassMatch[1]) {
      const fetchedBodyClasses = nextHtmlBodyClassMatch[1];
      if (debug) {
        console.log('[Barba Debug] syncManualBodyClasses: Class attribute of <body> in fetched (next) HTML:', fetchedBodyClasses);
      }
      document.body.className = fetchedBodyClasses;
      if (debug) {
        console.log('[Barba Debug] syncManualBodyClasses: document.body.className AFTER manual sync attempt:', document.body.className);
      }
    } else if (debug) {
      console.warn('[Barba Debug] syncManualBodyClasses: No class attribute found on <body> in fetched (next) HTML.');
    }
  } else if (debug) {
    console.warn('[Barba Debug] syncManualBodyClasses: Could not find <body> tag in fetched (next) HTML.');
  }
}



/**
 * Fades out an element.
 * @param {HTMLElement} element - The element to fade out.
 * @param {number} duration - The duration of the fade in milliseconds.
 * @returns {Promise<void>} A promise that resolves when the animation is complete.
 */
export async function fadeOut(element, duration = 300) {
  return new Promise(resolve => {
    if (!element) {
      resolve();
      return;
    }
    element.style.transition = `opacity ${duration / 1000}s ease-out`;
    element.style.opacity = '0';
    setTimeout(resolve, duration);
  });
}

/**
 * Fades in an element.
 * @param {HTMLElement} element - The element to fade in.
 * @param {number} duration - The duration of the fade in milliseconds.
 * @returns {Promise<void>} A promise that resolves when the animation is complete.
 */
export async function fadeIn(element, duration = 300) {
  return new Promise(resolve => {
    if (!element) {
      resolve();
      return;
    }
    // Opacity should be set to 0 before calling fadeIn, typically in beforeEnter or CSS.
    element.style.transition = `opacity ${duration / 1000}s ease-in`;
    element.style.opacity = '1';
    setTimeout(resolve, duration);
  });
}
